import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "@/contexts/AuthContext";

const SUGGESTIONS = [
  "Introduce myself to a new dealer and offer a 20-minute F&I menu review",
  "Announce our new Essentials New Car coverage with 12 yr / 125k eligibility",
  "Quarterly F&I industry update with three trends and a call to action",
  "We miss you — re-engage a dealer that hasn't written a contract in 90 days",
  "Reminder about the upcoming rate change and last call to lock current pricing",
];
const TONES = ["professional", "friendly", "urgent", "educational"];

export function AiBuildDialog({ open, onOpenChange, onBuilt }) {
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("professional");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (prompt.trim().length < 5) { toast.error("Describe the email you want"); return; }
    setBusy(true);
    try { const { data } = await api.post("/email-campaigns/ai-build", { prompt, tone }); onBuilt(data); onOpenChange(false); setPrompt(""); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl" data-testid="ai-build-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /> Build with Email AI</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6C757D]">Tell the AI who you're writing to and what you want them to do. It drafts a subject line and a full block layout you can rearrange and edit.</p>
        <div className="flex flex-wrap gap-1.5">{SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => setPrompt(s)} className="text-left text-xs px-2.5 py-1.5 rounded-sm border border-[#E9ECEF] bg-[#F8F9FA] hover:border-[#D4AF37] text-[#0B132B]" data-testid="ai-suggestion">{s}</button>)}</div>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} placeholder="Plan, build, modify anything…" className="rounded-sm text-sm" data-testid="ai-prompt" />
        <div className="flex items-center gap-1.5 text-xs"><span className="text-[#6C757D]">Tone:</span>{TONES.map((t) => <button key={t} type="button" onClick={() => setTone(t)} className={`px-2 py-1 rounded-sm border capitalize ${tone === t ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white border-[#E9ECEF] text-[#0B132B]"}`} data-testid={`ai-tone-${t}`}>{t}</button>)}</div>
        <p className="text-[10px] text-[#6C757D]">Email AI can make mistakes. Please double-check the draft before sending.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={run} disabled={busy} className="rounded-sm bg-[#0B132B] text-white" data-testid="ai-build-run">{busy ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Sparkles size={12} className="mr-1.5" />} {busy ? "Drafting…" : "Build email"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
